<template>
    <BasePicker ref="basePicker" v-bind="{...basePickerProps}">
        <template #default="{datePickerPos}">
            <vue-date-picker
                ref="picker"
                :model-value="dateModel"
                inline
                locale="nb"
                auto-apply
                arrow-navigation
                class="shadow-xl"
                :dark="isDark"
                :format-locale="nb"
                :min-date="SYS_MIN_DATE"
                :enable-time-picker="false"
                :config="{onClickOutside: (e: any) => handleOnSubmit(e)}"
                :style="datePickerPos"
                @update:model-value="onInternalPickerUpdate"
                @keydown.enter="handleOnEnter"
                @blur="emit('blur')"
            />
        </template>
    </BasePicker>
</template>

<script setup lang="ts">
import {addMonths, subMonths} from 'date-fns'
import {computed, nextTick, PropType, ref, useTemplateRef} from 'vue'
import {nb} from 'date-fns/locale'
import VueDatePicker from '@vuepic/vue-datepicker'
import {formattedDate} from '@/services/formatter.service'
import {ToastType, usePanelToast} from '@tritt/multipanel'
import {parseString, SYS_MIN_DATE} from '@/dateUtils/dateUtils'
import {LINE_COLUMN} from '@/transaction/create-transaction/EntryLineInstance'
import BasePicker from '@/dateUtils/BasePicker.vue'
import {HTMLInputElement} from 'happy-dom'
import {isDark} from '@/theme/theme.service'

const props = defineProps({
    dateId: String,
    size: {
        type: String,
        default: 'md'
    },
    label: String,
    hideCalenderIcon: {
        type: Boolean,
        default: false
    },
    isError: {
        type: Boolean,
        default: false
    },
    isDisabled: Boolean,
    dateFormat: {
        type: String,
        default: undefined
    },
    dataColId: {
        type: String as PropType<LINE_COLUMN>,
        default: undefined
    },
    yearColorHint: {
        type: Boolean,
        default: true
    },
    disableBackgroundColorClass: {
        type: Boolean,
        default: false
    },
})

const basePickerProps = computed(() => ({
    ...props,
    year: year.value,
    dateStr: dateStr.value,
    pageUp,
    pageDown,
    handleOnSubmit,
    focusPicker
}))

const dateModel = defineModel<Date>()
const year = computed(() => dateModel.value?.getFullYear().toString())
const dateStr = computed(() => formattedDate(dateModel.value, props.dateFormat))
const emit = defineEmits(['internal-enter', 'blur', 'hide-calendar'])

const {showPanelToast} = usePanelToast()
const picker = ref()
const basePicker = useTemplateRef<InstanceType<typeof BasePicker>>('basePicker')

const pageUp = () => dateModel.value = subMonths((dateModel.value as Date)!, 1)

const pageDown = () => dateModel.value = addMonths((dateModel.value as Date)!, 1)

const handleOnSubmit = (event: KeyboardEvent) => {
    const inputStr = (event?.target as unknown as HTMLInputElement)?.value
    const parsedDate = parseString(inputStr, dateModel?.value || new Date())
    if (!parsedDate) {
        hideCalendar()
        return
    }

    const isValidDate = parsedDate >= SYS_MIN_DATE
    dateModel.value = isValidDate ? parsedDate : SYS_MIN_DATE

    //TODO: Datepicker should not know about due date or toasts and stuff...
    if (!isValidDate) {
        showPanelToast('Forfallsdato endret til: ' + formattedDate(SYS_MIN_DATE), undefined, ToastType.WARNING)
    }

    hideCalendar()
}

const onInternalPickerUpdate = (value: any) => {
    dateModel.value = value

    nextTick(() => {
        hideCalendar()
    })
}

const focusBasePicker = () => basePicker?.value?.focus()
const focusPicker = () => picker?.value?.$el.querySelectorAll('.dp__outer_menu_wrap .dp__menu')[0].focus()

const hideCalendar = () => {
    basePicker?.value?.hide()
    emit('hide-calendar')
}

const handleOnEnter = () => {
    emit('internal-enter')
}

defineExpose({
    focus: focusBasePicker,
})

</script>

<style>
</style>